import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import './AISummary.css';
import { aiApi } from '../api/aiApi';

import AISummarySkeleton from './ai-summary/AISummarySkeleton';
import AISummaryError from './ai-summary/AISummaryError';
import AISummaryLocked from './ai-summary/AISummaryLocked';
import AISummaryContent from './ai-summary/AISummaryContent';
import AIPromptsCarousel from './ai-summary/AIPromptsCarousel';
import AIFeedback from './ai-summary/AIFeedback';
import AIFollowUp from './ai-summary/AIFollowUp';
// import AIReferences from './ai-summary/AIReferences';
import AIContinueConversation from './ai-summary/AIContinueConversation';

const getCurrentUserId = () => window.IM_SEARCH_DATA?.userId || null;

const appendUserIdToParams = (params) => {
    const userId = getCurrentUserId();
    if (userId) {
        params.set('userId', String(userId));
    }
    return params;
};

export default function AISummary({ setConsentOpen, setPaywallOpen, retrySummary, setRetrySummary }) {
    const [isLoading, setIsLoading] = useState(false);
    const [summary, setSummary] = useState('');
    const [suggestedPrompts, setSuggestedPrompts] = useState([]);
    const [conversationId, setConversationId] = useState(null);
    const [chatUrl, setChatUrl] = useState('/atlasassistant/');
    const [references, setReferences] = useState([]);
    const [error, setError] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [followUpQuestion, setFollowUpQuestion] = useState('');
    const [liked, setLiked] = useState(false);
    const [disliked, setDisliked] = useState(false);
    const summaryRef = useRef(null);
    const chatUrlRef = useRef('/atlasassistant/');
    const conversationIdRef = useRef(null);
    
    const searchOptions = useSelector((state) => state.search.searchOptions);
    const searchQuery = searchOptions?.searchQuery || '';
    const contentResults = useSelector((state) => state.search.contentResults);
    const showResultsLoading = useSelector((state) => state.search.showResultsLoading);
    const isLoggedIn = window.IM_SEARCH_DATA?.isLoggedIn;
    const isAIEnabled = !!window.IM_SEARCH_DATA?.isAIEnabled;
    const chatLeftDisclaimer = window.IM_SEARCH_DATA?.chatLeftDisclaimer || '';
    const chatLogo  = window.IM_SEARCH_DATA?.chatLogo  || '';

    useEffect(() => {
        if (!isAIEnabled) return;
        if (localStorage.getItem("dom_ai_consent_declined") === "true") return;
        if (searchQuery && searchQuery.trim().length > 0) {
            fetchAISummary();
            setIsExpanded(false);
            setLiked(false);
            setDisliked(false);
        } else {
            setSummary('');
            setSuggestedPrompts([]);
            setConversationId(null);
            setReferences([]);
            setError(null);
            setIsLoading(false);
            setIsExpanded(false);
        }
    }, [searchQuery]);

    useEffect(() => {
        if (!isAIEnabled) return;
        if (localStorage.getItem("dom_ai_consent_declined") === "true") return;
        if (retrySummary) {
            fetchAISummary();
            setRetrySummary(false);
        }
    }, [retrySummary]);

    // Expose a global so vanilla JS (citation popover) can trigger a source prompt.
    useEffect(() => {
        window.imTriggerSourcePrompt = (prompt) => {
            const params = appendUserIdToParams(new URLSearchParams());
            params.set('message', prompt);
            if (conversationIdRef.current) {
                params.set('conversationId', conversationIdRef.current);
            }
            window.location.href = `${chatUrlRef.current}?${params.toString()}`;
        };
        return () => {
            delete window.imTriggerSourcePrompt;
        };
    }, []);

    if (!isAIEnabled) return null;

    const fetchAISummary = async () => {
        setIsLoading(true);
        setError(null);
        setSummary('');


        try {
            const resultIds = contentResults?.posts?.map((post) => post.ID) || [];

            const response = await aiApi.fetchSummary(searchQuery, resultIds.slice(0, 10));

            if (response.status === 451) {
                setConsentOpen(true);
                setIsLoading(false);
                return;
            }

            if (response.status === 402) {
                setPaywallOpen(true);
                setIsLoading(false);
                return;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    setIsLoading(false);
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6);
                        if (data.trim() === '') continue;

                        try {
                            const parsed = JSON.parse(data);

                            if (parsed.type === 'status') {

                            } else if (parsed.type === 'chunk') {
                                setSummary((prev) => prev + parsed.message);
                            } else if (parsed.type === 'result') {
                                setSummary(parsed.message);
                                if (parsed.conversationId) {
                                    setConversationId(parsed.conversationId);
                                    conversationIdRef.current = parsed.conversationId;
                                }
                                if (parsed.chatUrl) {
                                    setChatUrl(parsed.chatUrl);
                                    chatUrlRef.current = parsed.chatUrl;
                                }
                                if (parsed.suggestedPrompts) setSuggestedPrompts(parsed.suggestedPrompts);
                                if (parsed.references) setReferences(parsed.references);
                            } else if (parsed.type === 'error') {
                                throw new Error(parsed.message || 'AI summary failed');
                            }
                        } catch (e) {
                            console.error('Error parsing SSE data:', e, data);
                        }
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching AI summary:', err);
            setError(err.message);
            setIsLoading(false);
        }
    };

    const handlePromptClick = (prompt) => {
        const params = appendUserIdToParams(new URLSearchParams());
        
        const messageWithContext = searchQuery
            ? `With regards to the search "${searchQuery}": ${prompt}`
            : prompt;
        
        params.set('message', messageWithContext);
        if (conversationId) {
            params.set('conversationId', conversationId);
        }
        window.location.href = `${chatUrl}?${params.toString()}`;
    };

    const handleFollowUpSubmit = (e) => {
        e.preventDefault();
        if (followUpQuestion.trim()) {
            const params = appendUserIdToParams(new URLSearchParams());
            
            // create message with Search context
            const messageWithContext = searchQuery
                ? `With regards to the search "${searchQuery}": ${followUpQuestion}`
                : followUpQuestion;
            
            params.set('message', messageWithContext);
            if (conversationId) {
                params.set('conversationId', conversationId);
            }
            window.location.href = `${chatUrl}?${params.toString()}`;
        }
    };

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };
    
    const handleFeedback = async (feedback) => {
        if (!conversationId) return;

        if (feedback === 'positive' && liked) {
            feedback = null;
        } else if (feedback === 'negative' && disliked) {
            feedback = null;
        }

        if (feedback === 'positive') {
            setLiked(true);
            setDisliked(false);
        } else if (feedback === 'negative') {
            setDisliked(true);
            setLiked(false);
        } else {
            setLiked(false);
            setDisliked(false);
        }

        try {
            const response = await aiApi.submitFeedback(conversationId, feedback);

            if (!response.ok) {
                const data = await response.json();
                console.error('Feedback submission failed:', data.message);
            }
        } catch (err) {
            console.error('Error submitting feedback:', err);
        }
    };

    if (!isLoggedIn) {
        return <AISummaryLocked chatLogo={chatLogo} currentUrl={window.location.href} />;
    }

    if (!searchQuery || searchQuery.trim().length === 0) {
        return null;
    }

    const hasDeclined = localStorage.getItem("dom_ai_consent_declined") === "true";
    if (hasDeclined) {
        return null;
    }

    if (isLoading || showResultsLoading) {
        return <AISummarySkeleton />;
    }

    if (error) {
        return <AISummaryError error={error} />;
    }

    if (!summary) {
        return null;
    }

    return (
        <div className="ai-summary-container">
            <div className={`ai-summary-card ai-summary-data ${isExpanded ? 'expanded' : 'collapsed'}`}>
                <div className="ai-summary-header">
                    <div className="ai-summary-icon">
                        <img src={chatLogo} alt="Atlas AI" />
                    </div>
                    <h3>AI Overview</h3>
                </div>

                <div className="ai-summary-scrollable">
                    <div className="ai-summary-content" ref={summaryRef}>
                        <AISummaryContent summary={summary} />
                    </div>

                    <AIContinueConversation
                        suggestedPrompts={suggestedPrompts}
                        handlePromptClick={handlePromptClick}
                    />

                    {/* <AIReferences references={references} /> */}

                    <AIFollowUp 
                        followUpQuestion={followUpQuestion} 
                        setFollowUpQuestion={setFollowUpQuestion} 
                        handleFollowUpSubmit={handleFollowUpSubmit} 
                    />

                    <div 
                        className="ai-summary-arrow-response-message chat-disclaimer"
                        dangerouslySetInnerHTML={{ __html: chatLeftDisclaimer }}
                    />
                </div>

                {!isExpanded && (
                    <div className="ai-summary-toggle">
                        <button className="ai-summary-toggle-btn" onClick={toggleExpanded}>
                            Show More
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M6 9L12 15L18 9"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                )}
                
                <AIFeedback 
                    handleFeedback={handleFeedback} 
                    liked={liked} 
                    disliked={disliked} 
                    summaryRef={summaryRef}
                />
            </div>
        </div>
    );
}
